import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Alert, Button, Card, Empty, Space, Spin, Typography, notification } from "antd";
import httpclient from "../../service/httpclient";
import { UserContext } from "../../App";
import "./streaming-activate.scss";

const { Title, Paragraph, Text } = Typography;

function StreamingActivate() {
  const { itemType, itemId } = useParams();
  const history = useHistory();
  const { updateUserInfo } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [redeemingVoucherId, setRedeemingVoucherId] = useState(null);
  const [error, setError] = useState("");
  const [target, setTarget] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [alreadyHasAccess, setAlreadyHasAccess] = useState(false);

  const validItemType = itemType === "series" || itemType === "stream";
  const numericItemId = Number(itemId);
  const validItemId = Number.isFinite(numericItemId) && numericItemId > 0;
  const targetLabel = useMemo(
    () => (itemType === "series" ? "直播课" : "直播场次"),
    [itemType]
  );

  useEffect(() => {
    if (!localStorage.token) {
      window.loginRedirectUrl = window.location.pathname + window.location.search;
      window.location.href = "/user/login";
      return;
    }

    if (!validItemType || !validItemId) {
      setAlreadyHasAccess(false);
      setError("直播内容信息无效");
      setLoading(false);
      return;
    }

    setLoading(true);
    setAlreadyHasAccess(false);
    httpclient
      .getMatchingStreamingVouchers(itemType, numericItemId)
      .then((response) => {
        setTarget(response.target || null);
        setAlreadyHasAccess(Boolean(response.already_has_access));
        setVouchers(response.vouchers || []);
        setError("");
      })
      .catch((err) => {
        setAlreadyHasAccess(false);
        setError(err?.errMsg || err?.message || "加载直播兑换券失败");
      })
      .finally(() => setLoading(false));
  }, [itemType, validItemType, validItemId, numericItemId]);

  function getSafeReturnUrl(defaultPath) {
    const returnUrl = localStorage.getItem("streaming_activation_return_url");
    if (returnUrl && returnUrl.startsWith("/") && !returnUrl.startsWith("//")) {
      return returnUrl;
    }

    return defaultPath;
  }

  function handleRedeem(voucherId) {
    if (!localStorage.token) {
      window.loginRedirectUrl = window.location.pathname + window.location.search;
      window.location.href = "/user/login";
      return;
    }

    setRedeemingVoucherId(voucherId);
    httpclient
      .redeemStreamingVoucher(voucherId, itemType, numericItemId)
      .then((response) => {
        notification.success({
          message: "激活成功",
          description: response?.target?.title
            ? `已解锁 ${response.target.title}`
            : "已成功解锁直播内容",
        });

        if (updateUserInfo) {
          updateUserInfo();
        }

        localStorage.setItem("streaming_purchase_success", "true");
        const returnUrl = getSafeReturnUrl(`/streaming/${itemType}/${numericItemId}`);
        localStorage.removeItem("streaming_activation_return_url");
        window.location.href = returnUrl;
      })
      .catch((err) => {
        notification.error({
          message: "激活失败",
          description: err?.errMsg || err?.message || "请稍后重试",
        });
      })
      .finally(() => setRedeemingVoucherId(null));
  }

  return (
    <div className="streaming-activate-page">
      <Card className="streaming-activate-card">
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <div className="streaming-activate-header">
            <Button type="link" onClick={() => history.goBack()} className="back-button">
              返回
            </Button>
            <Title level={3}>选择直播兑换券</Title>
            <Paragraph className="header-description">
              选择您账户中的直播兑换券，用于解锁当前{targetLabel}。
            </Paragraph>
          </div>

          {loading ? (
            <div className="streaming-activate-loading">
              <Spin size="large" />
            </div>
          ) : (
            <>
              {error ? (
                <Alert type="error" message={error} showIcon />
              ) : (
                <>
                  {target && (
                    <Card size="small" className="target-card">
                      <Space direction="vertical" size={8}>
                        <Text type="secondary">待激活内容</Text>
                        <Title level={4}>{target.title || `${targetLabel} #${itemId}`}</Title>
                        {target.series_title && itemType === "stream" && (
                          <Text type="secondary">所属直播课：{target.series_title}</Text>
                        )}
                        {target.description && (
                          <Paragraph className="target-description">
                            {target.description}
                          </Paragraph>
                        )}
                      </Space>
                    </Card>
                  )}

                  {alreadyHasAccess ? (
                    <Alert
                      type="info"
                      showIcon
                      message="该直播内容已在您的账户中解锁，无需再使用兑换券。"
                      action={
                        <Button
                          size="small"
                          type="primary"
                          onClick={() => {
                            window.location.href = getSafeReturnUrl(
                              `/streaming/${itemType}/${numericItemId}`
                            );
                          }}
                        >
                          返回直播页
                        </Button>
                      }
                    />
                  ) : vouchers.length === 0 ? (
                    <Empty
                      description={`您的账户中暂无可用于当前${targetLabel}的直播兑换券`}
                    />
                  ) : (
                    <div className="voucher-list">
                      {vouchers.map((voucher) => (
                        <Card
                          key={voucher.id}
                          className="streaming-voucher-card"
                          size="small"
                        >
                          <div className="voucher-card-main">
                            <div className="voucher-copy">
                              <Title level={4}>{voucher.name}</Title>
                              {voucher.description && (
                                <Paragraph>{voucher.description}</Paragraph>
                              )}
                            </div>
                            <Button
                              type="primary"
                              loading={redeemingVoucherId === voucher.id}
                              disabled={redeemingVoucherId !== null}
                              onClick={() => handleRedeem(voucher.id)}
                            >
                              使用此兑换券
                            </Button>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Space>
      </Card>
    </div>
  );
}

export default StreamingActivate;
